import { createEnv } from '@t3-oss/env-core'
import { z } from 'zod'

// TODO Dynamic defaults based on BUILD_ENV

/**
 * With Server-side rendered React (Remix, Next.js, and upcoming React Server Components), there
 * is not clean abstraction to safely way to distinguish and prevent leaking server-side and client side variables.
 *
 * This uses env.t3.org to create an env proxy to safely handle the client/server boundary of SSR apps.
 *
 * Includes:
 * TypeScript aware -- No more `process.env.MY_VAR as string`
 * Zod for validations, transformations, and defaults
 * Validates both at build time and runtime
 * IDE auto-complete
 * JSDoc support
 * Optional and defaults
 *
 * */
export const env = createEnv({
    /*
     * Specify what prefix the client-side variables must have.
     * This is enforced both on type-level and at runtime.
     */
    clientPrefix: 'REACT_APP_',
    /*
     * Serverside Environment variables, not available on the client.
     * Will throw if you access these variables on the client.
     * Use empty object if not using SSR
     */
    server: {},
    /*
     * Environment variables available on the client (and server).
     *
     * 💡 You'll get type errors if these are not prefixed with the clientPrefix.
     *
     * Does not yet handle NODE_ENV, or some [builder exposed variables](https://vitejs.dev/guide/env-and-mode.html#env-variables).
     * Continue to access these via `process.env` or `import.meta`
     * https://github.com/t3-oss/t3-env/issues/28
     *
     */
    client: {
        /** Environment deployed to: e.g. dev, stage, prod... */
        REACT_APP_BUILD_ENV: z.string().default('dev'),

        /** Used by Footer to show build number */
        REACT_APP_BUILD_NUMBER: z.coerce.number().int().transform(String).default(-1),
        /** Used by Footer to show build timestamp, and for new version checks. */
        REACT_APP_BUILD_TIMESTAMP: z.string().datetime().default(new Date(0).toISOString()),
        /** Used by Footer to show build branch */
        REACT_APP_BRANCH_NAME: z.string().default('unknown'),

        /** AWS AppSync endpoint */
        REACT_APP_GRAPHQL_URI: z.string().url(),
        /** AWs AppSync API KEY -- Used for Password reset */
        REACT_APP_GRAPHQL_API_KEY: z.string(),
        /** AWS AppSync endpoint */
        REACT_APP_UNIFIED_GRAPHQL_URI: z.string().url(),
        /** AWS AppSync API KEY -- Used for Codegen introspection */
        REACT_APP_UNIFIED_GRAPHQL_API_KEY: z.string(),
        /** AWS AppSync API KEY -- Used for overiding the default API Key for testing */
        REACT_APP_UNIFIED_GRAPHQL_API_TOKEN_OVERRIDE: z.string().optional(),
        /** AWS AppSync auth */
        REACT_APP_APPSYNC_AUTHENTICATION_TYPE: z.string().default('AMAZON_COGNITO_USER_POOLS'),
        /** AWS AppSync region */
        REACT_APP_APPSYNC_REGION: z.string().default('us-west-2'),

        /** Jasper reports endpoint */
        REACT_APP_REPORT_BASE_URL: z.string().url(),
        /** Different login mechanism for > v6 */
        REACT_APP_REPORTS_VERSION: z.coerce.number().positive().int().default(6),

        /** Always show React Hook Form debug below form */
        REACT_APP_RHF_DEVTOOLS: z.coerce.boolean().default(false),

        /** Bitly URL Shortener config. If undefined, will return original url */
        REACT_APP_BITLY_ACCESS_TOKEN: z.string().optional(),
        /** Bitly URL Shortener config. If undefined, will return original url */
        REACT_APP_BITLY_CUSTOM_DOMAIN: z.string().optional(),
        /** Bitly URL Shortener config. If undefined, will return original url */
        REACT_APP_BITLY_GROUP_GUID: z.string().optional(),

        /** Google Analytics. Will not initalize if missing */
        REACT_APP_GOOGLE_ANALYTICS_TRACKING_CODE: z.string().optional(),

        /** HotJar id. Will not initalize if missing */
        REACT_APP_HOTJAR_ID: z.coerce.number().optional(),
        /** HotJar version. Will not initalize if missing */
        REACT_APP_HOTJAR_SNIPPET_VERSION: z.coerce.number().optional(),

        /** Enable WhyDidYouRender devtool */
        REACT_APP_WHY_DID_YOU_RENDER: z.coerce.boolean().default(false),

        /** Dynamsoft key, may be base64 encoded */
        REACT_APP_DWT_KEY: z.string(),
        /** If true, parse REACT_APP_DWT_KEY as base64 */
        REACT_APP_DWT_KEY_ENCODED: z.boolean().default(true),

        /** AWS Cognito */
        REACT_APP_COGNITO_REGION: z.string(),
        /** AWS Cognito */
        REACT_APP_COGNITO_USER_POOL_ID: z.string(),
        /** AWS Cognito */
        REACT_APP_COGNITO_APP_CLIENT_ID: z.string(),

        /** BugSnag */
        REACT_APP_BUGSNAG_API_KEY: z.string().optional(),
        /** BugSnag */
        REACT_APP_BUGSNAG_ENV: z.string().optional(),

        REACT_APP_AUDIT_URI: z.string(),
        REACT_APP_AUDIT_API_KEY: z.string(),

        REACT_APP_LAUNCHDARKLY_CLIENT_ID: z.string(),
    },
    /**
     * What object holds the environment variables at runtime.
     * Often simply `process.env` or `import.meta.env`.
     *
     * Possible issues:
     *
     * https://env.t3.gg/docs/nextjs
     *
     * Due to how some bundlers treat environment variables on Edge and Client,
     * we may need to manually destructure them to make sure all are included in bundle.
     *
     * @example
     * ```
     * // Simple, but doesn't always work
     * runtimeEnv: process.env,
     * ```
     *
     * @example
     * ```
     * // Manually destructure to make sure all are included in bundle
     * runtimeEnv: {
     *   DATABASE_URL: process.env.DATABASE_URL,
     *   OPEN_AI_API_KEY: process.env.OPEN_AI_API_KEY,
     *   NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
     * }
     * ```
     *
     * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
     *
     */
    runtimeEnv: {
        REACT_APP_BUILD_ENV: process.env.REACT_APP_BUILD_ENV,
        REACT_APP_BUILD_NUMBER: process.env.REACT_APP_BUILD_NUMBER,
        REACT_APP_BUILD_TIMESTAMP: process.env.REACT_APP_BUILD_TIMESTAMP,
        REACT_APP_BRANCH_NAME: process.env.REACT_APP_BRANCH_NAME,
        REACT_APP_GRAPHQL_URI: process.env.REACT_APP_GRAPHQL_URI,
        REACT_APP_GRAPHQL_API_KEY: process.env.REACT_APP_GRAPHQL_API_KEY,
        REACT_APP_UNIFIED_GRAPHQL_URI: process.env.REACT_APP_UNIFIED_GRAPHQL_URI,
        REACT_APP_UNIFIED_GRAPHQL_API_KEY: process.env.REACT_APP_UNIFIED_GRAPHQL_API_KEY,
        REACT_APP_UNIFIED_GRAPHQL_API_TOKEN_OVERRIDE: process.env.REACT_APP_UNIFIED_GRAPHQL_API_TOKEN_OVERRIDE,
        REACT_APP_APPSYNC_AUTHENTICATION_TYPE: process.env.REACT_APP_APPSYNC_AUTHENTICATION_TYPE,
        REACT_APP_APPSYNC_REGION: process.env.REACT_APP_APPSYNC_REGION,
        REACT_APP_REPORT_BASE_URL: process.env.REACT_APP_REPORT_BASE_URL,
        REACT_APP_REPORTS_VERSION: process.env.REACT_APP_REPORTS_VERSION,
        REACT_APP_RHF_DEVTOOLS: process.env.REACT_APP_RHF_DEVTOOLS,
        REACT_APP_BITLY_ACCESS_TOKEN: process.env.REACT_APP_BITLY_ACCESS_TOKEN,
        REACT_APP_BITLY_CUSTOM_DOMAIN: process.env.REACT_APP_BITLY_CUSTOM_DOMAIN,
        REACT_APP_BITLY_GROUP_GUID: process.env.REACT_APP_BITLY_GROUP_GUID,
        REACT_APP_GOOGLE_ANALYTICS_TRACKING_CODE: process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_CODE,
        REACT_APP_HOTJAR_ID: process.env.REACT_APP_HOTJAR_ID,
        REACT_APP_HOTJAR_SNIPPET_VERSION: process.env.REACT_APP_HOTJAR_SNIPPET_VERSION,
        REACT_APP_WHY_DID_YOU_RENDER: process.env.REACT_APP_WHY_DID_YOU_RENDER,
        REACT_APP_DWT_KEY: process.env.REACT_APP_DWT_KEY,
        REACT_APP_DWT_KEY_ENCODED: process.env.REACT_APP_DWT_KEY_ENCODED,
        REACT_APP_COGNITO_REGION: process.env.REACT_APP_COGNITO_REGION,
        REACT_APP_COGNITO_USER_POOL_ID: process.env.REACT_APP_COGNITO_USER_POOL_ID,
        REACT_APP_COGNITO_APP_CLIENT_ID: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
        REACT_APP_BUGSNAG_API_KEY: process.env.REACT_APP_BUGSNAG_API_KEY,
        REACT_APP_BUGSNAG_ENV: process.env.REACT_APP_BUGSNAG_ENV,
        REACT_APP_AUDIT_URI: process.env.REACT_APP_AUDIT_URI,
        REACT_APP_AUDIT_API_KEY: process.env.REACT_APP_AUDIT_API_KEY,
        REACT_APP_LAUNCHDARKLY_CLIENT_ID: process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID,
    },

    skipValidation: process.env.NODE_ENV === 'test' || process.env.SKIP_ENV_VALIDATION === 'true',
})
